import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Timer nieaktywności — użytkownik zostaje wylogowany po `timeoutSec` bez interakcji.
 * Każdy ruch myszy / klawiatura / kliknięcie resetuje licznik.
 * Zwraca `remaining` (sekundy) do wyświetlenia obok przycisku wyloguj.
 */
export function useIdleTimer(timeoutSec, onTimeout) {
  const [remaining, setRemaining] = useState(timeoutSec);
  const remainingRef = useRef(timeoutSec);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const reset = useCallback(() => {
    remainingRef.current = timeoutSec;
    setRemaining(timeoutSec);
  }, [timeoutSec]);

  useEffect(() => {
    // Reset przy aktywności
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    let throttleTs = 0;
    const handler = () => {
      const now = Date.now();
      if (now - throttleTs < 1000) return; // throttle 1s żeby nie spamić state
      throttleTs = now;
      remainingRef.current = timeoutSec;
      setRemaining(timeoutSec);
    };
    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));

    // Odliczanie co sekundę
    const interval = setInterval(() => {
      remainingRef.current -= 1;
      setRemaining(remainingRef.current);
      if (remainingRef.current <= 0) {
        clearInterval(interval);
        onTimeoutRef.current?.();
      }
    }, 1000);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handler));
      clearInterval(interval);
    };
  }, [timeoutSec]);

  return { remaining, reset };
}

/** Format sekund jako MM:SS */
export function formatTime(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}
